import React, { useCallback, useEffect, useState } from 'react';
import {
    API_APPROVE_REPAYMENT_REQUEST,
    API_BRANCH_REPAYMENT_HISTORY,
    API_MY_REPAYMENT_REQUESTS,
    API_PENDING_REPAYMENT_REQUESTS,
    API_REJECT_REPAYMENT_REQUEST,
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { apiJson } from '../utils/apiClient';
import { confirmAction } from '../utils/confirmAction';

const RepaymentApprovalRequests = () => {
    const { user } = useAuth();
    const [myRequests, setMyRequests] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [branchHistory, setBranchHistory] = useState([]);
    const [error, setError] = useState('');
    const [rejectingId, setRejectingId] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const canReviewRepayments = ['manager', 'admin', 'super_admin'].includes(user?.role_key);

    const loadRequests = useCallback(async () => {
        try {
            setError('');
            const requests = await apiJson(API_MY_REPAYMENT_REQUESTS, { method: 'POST', body: JSON.stringify({}) });
            setMyRequests(requests.data?.data || []);
            if (canReviewRepayments) {
                const pending = await apiJson(API_PENDING_REPAYMENT_REQUESTS, { method: 'POST', body: JSON.stringify({}) });
                setPendingRequests(pending.data?.data || []);
                const history = await apiJson(API_BRANCH_REPAYMENT_HISTORY, { method: 'POST', body: JSON.stringify({}) });
                setBranchHistory(history.data?.data || []);
            }
        } catch (requestError) {
            setError('Unable to load repayment requests.');
        }
    }, [canReviewRepayments]);

    useEffect(() => { void loadRequests(); }, [loadRequests]);

    const approve = async (requestId) => {
        if (!confirmAction('approve and post this repayment request')) return;
        const { data } = await apiJson(API_APPROVE_REPAYMENT_REQUEST, { method: 'POST', body: JSON.stringify({ request_id: requestId }) });
        if (!data?.status) { setError(data?.message || 'Unable to approve request.'); return; }
        await loadRequests();
    };

    const reject = async (requestId) => {
        if (!rejectionReason.trim()) { setError('Enter a rejection reason before rejecting this request.'); return; }
        if (!confirmAction('reject this repayment request')) return;
        const { data } = await apiJson(API_REJECT_REPAYMENT_REQUEST, { method: 'POST', body: JSON.stringify({ request_id: requestId, rejection_reason: rejectionReason }) });
        if (!data?.status) { setError(data?.message || 'Unable to reject request.'); return; }
        setRejectingId('');
        setRejectionReason('');
        await loadRequests();
    };

    const renderRows = (requests, managerActions = false) => requests.length ? requests.map((request) => (
        <tr key={request.id}>
            <td>#{request.id}</td><td>{request.branch_name}</td><td>{request.center_name}</td><td>{request.request_type}</td>
            <td>{request.item_count}</td><td>Rs. {Number(request.total_amount).toLocaleString('en-IN')}</td><td>{request.status}</td>
            <td>{request.submitted_by_username}</td>
            <td>{managerActions ? <div className="d-flex gap-2 flex-wrap">
                <button type="button" className="btn btn-sm btn-success" onClick={() => approve(request.id)}>Approve</button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setRejectingId(request.id)}>Reject</button>
                {rejectingId === request.id ? <div className="w-100 d-flex gap-2"><input value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} placeholder="Reason is required" /><button type="button" className="btn btn-sm btn-danger" onClick={() => reject(request.id)}>Confirm</button></div> : null}
            </div> : request.rejection_reason || '-'}</td>
        </tr>
    )) : <tr><td colSpan="9" className="text-center">No requests found.</td></tr>;

    return <div className="container my-5">
        <h2>Repayment Requests</h2>
        {error ? <div className="alert alert-danger">{error}</div> : null}
        {canReviewRepayments ? <><h4 className="mt-4">Pending Branch Approvals</h4><div className="table-responsive data-table-shell"><table className="table data-table"><thead><tr><th>Request</th><th>Branch</th><th>Center</th><th>Type</th><th>Items</th><th>Total</th><th>Status</th><th>Submitted By</th><th>Actions</th></tr></thead><tbody>{renderRows(pendingRequests, true)}</tbody></table></div></> : null}
        {canReviewRepayments ? <><h4 className="mt-4">Branch Repayment History</h4><div className="table-responsive data-table-shell"><table className="table data-table"><thead><tr><th>Request</th><th>Branch</th><th>Center</th><th>Type</th><th>Items</th><th>Total</th><th>Status</th><th>Submitted By</th><th>Decision / Reason</th></tr></thead><tbody>{renderRows(branchHistory)}</tbody></table></div></> : null}
        <h4 className="mt-4">My Repayment History</h4><div className="table-responsive data-table-shell"><table className="table data-table"><thead><tr><th>Request</th><th>Branch</th><th>Center</th><th>Type</th><th>Items</th><th>Total</th><th>Status</th><th>Submitted By</th><th>Decision / Reason</th></tr></thead><tbody>{renderRows(myRequests)}</tbody></table></div>
    </div>;
};

export default RepaymentApprovalRequests;
